import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import styles from 'components/admin/bulkAdminTable.module.css';
import { groupingBasisOf, formatGroupCell } from 'utils/groupLabel';

const TYPE_BADGE_CLASS = {
  Full: styles.badgeRoleMember,
  Guest: styles.badgeRoleGuest,
  Visitor: styles.badgeRoleVisitor,
};

// Badge labels are consistent with the user-role badges: an athlete's "Full" membership reads as "Member".
const TYPE_LABEL = {
  Full: 'Member',
  Guest: 'Guest',
  Visitor: 'Visitor',
};

// Renders a contact's WHOLE family of athletes as chips. An athlete whose membership type differs from the list's
// native type (e.g. a Visitor athlete showing under a Guest contact in the Guest Members list) gets a small type
// badge, so cross-type family members are visible at a glance instead of being hidden.
// See docs/multi-club-meet-visitors-design.html §4.2.
const MemberAthletesCell = ({ athletes, nativeType }) => {
  // The athlete's group as the selected club reads it: "(11M)", or "(Y6M)" for a club that groups by year level.
  const groupingBasis = useSelector((state) =>
    groupingBasisOf(state.user?.selectedClub)
  );

  if (!athletes || athletes.length === 0) {
    return <span className={styles.muted}>—</span>;
  }
  return (
    <div className={styles.athleteChips}>
      {athletes.map((a) => {
        const type = a.membershipType;
        const showBadge = !!type && type !== nativeType;
        return (
          <span key={a.athleteID} className={styles.athleteChip}>
            {`${a.firstName} ${a.lastName} (${formatGroupCell(groupingBasis, a.age)}${a.gender})`}
            {showBadge && (
              <span
                className={`${styles.badge} ${styles.badgeMini} ${
                  TYPE_BADGE_CLASS[type] || styles.badgeRoleMember
                }`}
              >
                {TYPE_LABEL[type] || type}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

MemberAthletesCell.propTypes = {
  athletes: PropTypes.array,
  // The list's native membership type ("Full" | "Guest" | "Visitor"); athletes of other types get a badge.
  nativeType: PropTypes.string,
};

export default MemberAthletesCell;
